import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, Building, CalendarCheck, Save, History, Undo2 } from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { AccountCombobox } from '../components/AccountCombobox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listFixedAssets, createFixedAsset, updateFixedAsset, deleteFixedAsset,
  postDepreciation, postAllDepreciation, reverseDepreciation, listAccounts,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);
const MONTHS_ID_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const formatDateId = (iso) => {
  if (!iso || iso.length < 10) return '—';
  const [y, m, d] = iso.split('-');
  return `${parseInt(d, 10)} ${MONTHS_ID_SHORT[parseInt(m, 10) - 1]} ${y}`;
};

const emptyForm = () => ({
  name: '', acquisition_date: todayIso(), acquisition_cost: '',
  useful_life_years: 4, salvage_value: '',
  asset_account_id: '', accum_account_id: '', expense_account_id: '',
  notes: '',
});

export default function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);

  const refresh = async () => {
    try {
      const [a, acc] = await Promise.all([listFixedAssets(), listAccounts({ is_active: true })]);
      setAssets(a);
      setAccounts(acc);
    } catch {
      toast.error('Gagal memuat data');
    }
  };

  useEffect(() => { refresh(); }, []);

  const assetAccounts = useMemo(() => accounts.filter((a) => a.type === 'asset'), [accounts]);
  const expenseAccounts = useMemo(() => accounts.filter((a) => a.type === 'expense'), [accounts]);

  const openCreate = () => {
    const asset = accounts.find((a) => a.code === '1-2100');
    const accum = accounts.find((a) => a.code === '1-2900');
    const expense = accounts.find((a) => a.code === '5-6000');
    setEditingId(null);
    setForm({
      ...emptyForm(),
      asset_account_id: asset?.id || '',
      accum_account_id: accum?.id || '',
      expense_account_id: expense?.id || '',
    });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || '',
      acquisition_date: a.acquisition_date || todayIso(),
      acquisition_cost: a.acquisition_cost ? formatThousands(String(a.acquisition_cost)) : '',
      useful_life_years: a.useful_life_years || 4,
      salvage_value: a.salvage_value ? formatThousands(String(a.salvage_value)) : '',
      asset_account_id: a.asset_account_id || '',
      accum_account_id: a.accum_account_id || '',
      expense_account_id: a.expense_account_id || '',
      notes: a.notes || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nama aset wajib');
    const cost = parseNumber(form.acquisition_cost);
    if (!cost || cost <= 0) return toast.error('Harga perolehan harus > 0');
    const yrs = Number(form.useful_life_years) || 0;
    if (yrs < 1) return toast.error('Umur manfaat minimal 1 tahun');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        acquisition_date: form.acquisition_date,
        acquisition_cost: cost,
        useful_life_years: yrs,
        salvage_value: parseNumber(form.salvage_value) || 0,
        asset_account_id: form.asset_account_id || '',
        accum_account_id: form.accum_account_id || '',
        expense_account_id: form.expense_account_id || '',
        notes: form.notes.trim(),
      };
      if (editingId) {
        await updateFixedAsset(editingId, payload);
        toast.success('Aset diperbarui');
      } else {
        await createFixedAsset(payload);
        toast.success('Aset ditambahkan');
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Hapus aset "${a.name}"? Jurnal penyusutan terkait juga akan dihapus.`)) return;
    try {
      await deleteFixedAsset(a.id);
      toast.success('Aset dihapus');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const postOne = async (a) => {
    if (!a.next_month) return toast.info('Aset sudah disusut penuh');
    try {
      const r = await postDepreciation(a.id, a.next_month);
      toast.success(`Jurnal ${r.journal_ref} otomatis dibuat di Jurnal Umum (${a.next_month})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mencatat');
    }
  };

  const reverseOne = async (a, month) => {
    if (!window.confirm(`Batalkan penyusutan ${a.name} untuk bulan ${month}?\nJurnal terkait di Jurnal Umum akan dihapus.`)) return;
    try {
      const r = await reverseDepreciation(a.id, month);
      toast.success(`Penyusutan ${month} dibatalkan (${r.journals_deleted} jurnal dihapus)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal membatalkan');
    }
  };

  const postAll = async () => {
    setPosting(true);
    try {
      const r = await postAllDepreciation();
      if (r.count > 0) toast.success(`${r.count} jurnal penyusutan dicatat`);
      else toast.info('Tidak ada aset yang perlu dicatat bulan ini');
      await refresh();
    } catch {
      toast.error('Gagal mencatat penyusutan massal');
    } finally {
      setPosting(false);
    }
  };

  return (
    <AccountingLayout>
      <div className="space-y-6" data-testid="assets-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
              Aset Tetap
            </h1>
            <p className="text-sm text-stone-500 mt-2 max-w-xl">
              Kelola aset dan catat penyusutan garis lurus secara otomatis. Tombol
              <span className="mx-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-medium">
                Catat
              </span>
              akan membuat jurnal penyusutan otomatis di Jurnal Umum.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={postAll}
              disabled={posting || assets.length === 0}
              data-testid="btn-post-all"
              className="h-11 px-4 border-stone-200 gap-2"
            >
              <CalendarCheck className="w-4 h-4" />
              {posting ? 'Mencatat…' : 'Catat Semua'}
            </Button>
            <Button
              onClick={openCreate}
              data-testid="btn-add-asset"
              className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5 rounded-lg gap-2"
            >
              <Plus className="w-4 h-4" />
              Tambah Aset
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
          {assets.length === 0 ? (
            <div className="py-16 text-center">
              <Building className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <div className="text-sm text-stone-500">Belum ada aset. Klik <b>Tambah Aset</b> untuk memulai.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50/50">
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Nama Aset</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Tgl Perolehan</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Harga Perolehan</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Penyusutan/bln</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Akumulasi</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Nilai Buku</th>
                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-5 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr key={a.id} data-testid={`asset-row-${a.id}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/40">
                    <td className="px-5 py-4">
                      <div className="font-medium text-stone-900">{a.name}</div>
                      <div className="text-xs text-stone-500">{a.useful_life_years} tahun</div>
                    </td>
                    <td className="px-5 py-4 font-mono text-stone-700 text-[13px] whitespace-nowrap">
                      {formatDateId(a.acquisition_date)}
                    </td>
                    <td className="px-5 py-4 font-mono text-stone-800 whitespace-nowrap">{formatRp(a.acquisition_cost)}</td>
                    <td className="px-5 py-4 font-mono text-stone-800 whitespace-nowrap">{formatRp(a.monthly_depreciation)}</td>
                    <td className="px-5 py-4 font-mono text-rose-600 whitespace-nowrap">{formatRp(a.accum_depreciation)}</td>
                    <td className="px-5 py-4 font-mono font-semibold text-stone-900 whitespace-nowrap">{formatRp(a.book_value)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {a.next_month ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => postOne(a)}
                            data-testid={`post-depr-${a.id}`}
                            title={`Catat penyusutan bulan ${a.next_month} — otomatis membuat jurnal penyusutan di Jurnal Umum (D: Beban Penyusutan, K: Akumulasi Penyusutan)`}
                            className="h-8 px-3 border-emerald-200 text-emerald-800 hover:bg-emerald-50 gap-1.5"
                          >
                            <CalendarCheck className="w-3.5 h-3.5" />
                            Catat {a.next_month}
                          </Button>
                        ) : (
                          <span className="text-xs text-stone-400 italic px-2">Susut penuh</span>
                        )}
                        {(a.posted_months || []).length > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                data-testid={`history-asset-${a.id}`}
                                title="Riwayat & batalkan penyusutan"
                                className="p-1.5 rounded hover:bg-stone-100 text-stone-500 relative"
                              >
                                <History className="w-4 h-4" />
                                <span className="absolute -top-1 -right-1 bg-emerald-700 text-white text-[9px] font-semibold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                                  {(a.posted_months || []).length}
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-64 p-0 bg-white">
                              <div className="px-3 py-2 border-b border-stone-200">
                                <div className="text-xs font-semibold text-stone-900">Riwayat Penyusutan</div>
                                <div className="text-[10px] text-stone-500">
                                  Klik "Batal" untuk menghapus jurnal bulan tsb.
                                </div>
                              </div>
                              <div className="max-h-64 overflow-y-auto py-1">
                                {[...(a.posted_months || [])].sort().map((m) => (
                                  <div
                                    key={m}
                                    data-testid={`history-row-${a.id}-${m}`}
                                    className="flex items-center justify-between px-3 py-1.5 hover:bg-stone-50 text-sm"
                                  >
                                    <div className="flex items-center gap-2">
                                      <CalendarCheck className="w-3.5 h-3.5 text-emerald-700" />
                                      <span className="font-mono text-[13px] text-stone-800">{m}</span>
                                      <span className="text-[11px] text-stone-500">
                                        {formatRp(a.monthly_depreciation)}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => reverseOne(a, m)}
                                      data-testid={`reverse-depr-${a.id}-${m}`}
                                      className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:bg-rose-50 rounded px-1.5 py-0.5 border border-transparent hover:border-rose-200"
                                    >
                                      <Undo2 className="w-3 h-3" />
                                      Batal
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <button
                          onClick={() => openEdit(a)}
                          data-testid={`edit-asset-${a.id}`}
                          className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(a)}
                          data-testid={`delete-asset-${a.id}`}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-500"
                          title="Hapus"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Dialog */}
        <Dialog
          open={open}
          onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}
        >
          <DialogContent className="sm:max-w-lg" data-testid="asset-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Aset' : 'Tambah Aset'}</DialogTitle>
              <DialogDescription>
                Penyusutan dihitung garis lurus: (Harga Perolehan − Nilai Sisa) ÷ (Umur × 12).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Nama Aset</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="misal: Mesin Siboasi"
                  data-testid="input-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tanggal Perolehan</Label>
                  <Input
                    type="date"
                    value={form.acquisition_date}
                    onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
                    data-testid="input-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Umur Manfaat (tahun)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                    data-testid="input-years"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Harga Perolehan (Rp)</Label>
                  <Input
                    value={form.acquisition_cost}
                    onChange={(e) => setForm({ ...form, acquisition_cost: formatThousands(e.target.value) })}
                    inputMode="numeric"
                    data-testid="input-cost"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nilai Sisa / Salvage (opsional)</Label>
                  <Input
                    value={form.salvage_value}
                    onChange={(e) => setForm({ ...form, salvage_value: formatThousands(e.target.value) })}
                    inputMode="numeric"
                    data-testid="input-salvage"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 pt-1 border-t border-stone-100">
                <div>
                  <Label className="text-xs">Akun Aset (Debit saat pembelian)</Label>
                  <AccountCombobox
                    value={form.asset_account_id}
                    onChange={(v) => setForm({ ...form, asset_account_id: v })}
                    accounts={assetAccounts}
                    testId="acc-asset"
                    placeholder="Pilih akun aset…"
                  />
                </div>
                <div>
                  <Label className="text-xs">Akumulasi Penyusutan (Kredit saat penyusutan)</Label>
                  <AccountCombobox
                    value={form.accum_account_id}
                    onChange={(v) => setForm({ ...form, accum_account_id: v })}
                    accounts={assetAccounts}
                    testId="acc-accum"
                    placeholder="Pilih akun akumulasi…"
                  />
                </div>
                <div>
                  <Label className="text-xs">Beban Penyusutan (Debit saat penyusutan)</Label>
                  <AccountCombobox
                    value={form.expense_account_id}
                    onChange={(v) => setForm({ ...form, expense_account_id: v })}
                    accounts={expenseAccounts}
                    testId="acc-expense"
                    placeholder="Pilih akun beban…"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Catatan (opsional)</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="btn-cancel">
                Batal
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-emerald-800 hover:bg-emerald-900 text-white gap-2"
                data-testid="btn-save"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Menyimpan…' : 'Simpan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AccountingLayout>
  );
}
